import {
  HttpContextToken,
  HttpErrorResponse,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpEvent,
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { OAuthService } from 'angular-oauth2-oidc';
import { Observable, from, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { environment } from '@et/environment';
import {
  TokenRefreshCoordinator,
  TokenRefreshUnavailableError,
} from '@et/guards';
import { buildOAuthAllowedResourceOrigins } from './resource-server-urls';

/** Marks a request as already retried once after a refresh, so the retry itself can't loop. */
const RETRIED_AFTER_REFRESH = new HttpContextToken<boolean>(() => false);

/**
 * Attaches the OIDC **id_token** (a signed RS256 JWT) as the `Authorization: Bearer`
 * header for requests to protected resource-server origins.
 *
 * Background: Better Auth's `oidcProvider` always issues an **opaque** `access_token`
 * (a random string stored server-side). The `id_token` is the only JWT in the token
 * response and contains all the claims (.NET services need: `sub`, `email`, `iss`,
 * `aud`, `exp`, `roles`). Using it as the Bearer token lets every .NET resource server
 * validate locally via the RS256 JWKS endpoint without introspection support.
 *
 * `OAuthModule.forRoot` is configured with `sendAccessToken: false` so the library
 * does not attach the opaque access_token first.
 *
 * A 401 from an allowed origin (e.g. the id_token expired while the tab was
 * backgrounded/asleep and the automatic-silent-refresh timer hasn't caught up yet) is
 * handled here as a fallback to the proactive `visibilitychange`/`focus` refresh in
 * `oauth.initializer.ts`: refresh once via `TokenRefreshCoordinator` and retry the
 * request with the fresh token, or force re-login if the refresh token is also dead.
 */
@Injectable()
export class IdTokenBearerInterceptor implements HttpInterceptor {
  private readonly oauth = inject(OAuthService);
  private readonly coordinator = inject(TokenRefreshCoordinator);
  private readonly allowedOrigins: ReadonlySet<string> = new Set(
    buildOAuthAllowedResourceOrigins(environment),
  );

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    const origin = this.originOf(req.url);
    if (!origin || !this.allowedOrigins.has(origin)) {
      return next.handle(req);
    }

    return next.handle(this.withBearer(req)).pipe(
      catchError((error: unknown) => {
        if (
          !(error instanceof HttpErrorResponse) ||
          error.status !== 401 ||
          req.context.get(RETRIED_AFTER_REFRESH)
        ) {
          return throwError(() => error);
        }

        return from(this.coordinator.ensureValidToken()).pipe(
          switchMap((refreshed) => {
            if (!refreshed) {
              this.coordinator.forceReauth(this.currentUrl());
              return throwError(() => error);
            }
            const retried = this.withBearer(req).clone({
              context: req.context.set(RETRIED_AFTER_REFRESH, true),
            });
            return next.handle(retried);
          }),
          // Issuer unreachable: keep the session (no forced re-login) and surface the
          // original 401 — the next focus/401 retries the refresh.
          catchError((refreshError: unknown) =>
            throwError(() =>
              refreshError instanceof TokenRefreshUnavailableError
                ? error
                : refreshError,
            ),
          ),
        );
      }),
    );
  }

  private withBearer(req: HttpRequest<unknown>): HttpRequest<unknown> {
    const idToken = this.oauth.getIdToken();
    if (!idToken) {
      return req;
    }
    return req.clone({ setHeaders: { Authorization: `Bearer ${idToken}` } });
  }

  private originOf(url: string): string | null {
    try {
      return new URL(url).origin;
    } catch {
      return null;
    }
  }

  private currentUrl(): string {
    return typeof window === 'undefined'
      ? '/'
      : window.location.pathname + window.location.search;
  }
}
